import type { InvestorProfile } from '@/api/custom';
import type { Thread } from '@/common/components/wavvest-planning/types';
import { getInvestorDisplayName } from '@/util/investor-display';

export interface ThreadGroup {
  key: string;
  label: string;
  threads: Thread[];
}

type ThreadGroupIdentity = Pick<ThreadGroup, 'key' | 'label'>;

const compareThreadsByUpdatedAtDesc = (a: Thread, b: Thread) =>
  new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();

export const groupThreads = (
  threads: Thread[],
  getGroupIdentity: (thread: Thread) => ThreadGroupIdentity,
): ThreadGroup[] => {
  const groups = new Map<string, ThreadGroup>();

  [...threads].sort(compareThreadsByUpdatedAtDesc).forEach((thread) => {
    const { key, label } = getGroupIdentity(thread);
    const existingGroup = groups.get(key);

    if (existingGroup) {
      existingGroup.threads.push(thread);
      return;
    }

    groups.set(key, { key, label, threads: [thread] });
  });

  return Array.from(groups.values());
};

export const getInvestorThreadGroup = (
  investorId: string | undefined,
  investors: InvestorProfile[],
): ThreadGroupIdentity => {
  if (!investorId) {
    return { key: 'unassigned', label: 'Unassigned' };
  }

  const investor = investors.find((candidate) => candidate.id === investorId);

  return {
    key: investorId,
    label: investor ? getInvestorDisplayName(investor) : 'Unknown client',
  };
};

export const findInvestorByCognitoId = (investors: InvestorProfile[], cognitoId: string | null) => {
  if (!cognitoId) {
    return undefined;
  }

  return investors.find((investor) => investor.cognito_id === cognitoId);
};

export const sortInvestorsByDisplayName = (investors: InvestorProfile[]): InvestorProfile[] =>
  [...investors].sort((a, b) => getInvestorDisplayName(a).localeCompare(getInvestorDisplayName(b)));
